import React, { useCallback, useEffect, useRef, useState } from "react";

import { createTelemetryEvent } from "../telemetry/Telemetry";
import { telemetryChannelName } from "../util/constants";

export enum TechSupportType {
  CSS = "CSS NA",
  SME = "OLT SME Team",
}

export type HasbotContextType = {
  showDialog: boolean;
  setShowDialog: React.Dispatch<React.SetStateAction<boolean>>;
  techSupportActive?: TechSupportType;
  setZopimChatActive: React.Dispatch<React.SetStateAction<boolean>>;
  zopimChatActive: boolean;
  setAiChatActive: React.Dispatch<React.SetStateAction<boolean>>;
  aiChatActive: boolean;
  setTechSupportActive: React.Dispatch<React.SetStateAction<TechSupportType | undefined>>;
  keyboardShortcutsActive: boolean;
  setKeyboardShortcutsActive: React.Dispatch<React.SetStateAction<boolean>>;
  openDialogBtnRef: React.RefObject<HTMLButtonElement | null>;
  unreadChatMessages: number;
};

export const HasbotContext = React.createContext<HasbotContextType>({} as HasbotContextType);

export const HasbotProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [showDialog, setShowDialog] = useState(false);
  const [zopimChatActive, setZopimChatActive] = useState(false);
  const [unreadChatMessages, setUnreadChatMessages] = useState(0);
  const [techSupportActive, setTechSupportActive] = useState<TechSupportType | undefined>();
  const [aiChatActive, setAiChatActive] = useState(false);
  const [keyboardShortcutsActive, setKeyboardShortcutsActive] = useState(false);
  const openDialogBtnRef = useRef<HTMLButtonElement>(null);

  const handleMessages = useCallback(() => {
    setUnreadChatMessages(prevState => prevState + 1);
  }, []);

  const handleMessageSent = useCallback((event: Event) => {
    // Telemetry: Message sent in chat
    const customEvent = event as CustomEvent<{ messageType: "offline" | "online" }>;
    const messageType = customEvent.detail?.messageType;
    const actionMeta = messageType === "offline" ? "offline message sent" : "online message sent";

    createTelemetryEvent({
      feature: "human sme chat",
      action: "send message",
      action_meta: actionMeta,
    }).fire(telemetryChannelName);
  }, []);

  useEffect(() => {
    setUnreadChatMessages(0);
  }, [showDialog]);

  useEffect(() => {
    window.addEventListener("newZopimMessage", handleMessages, false);
    return () => {
      window.removeEventListener("newZopimMessage", handleMessages, false);
    };
  }, [handleMessages]);

  useEffect(() => {
    window.addEventListener("zopimMessageSent", handleMessageSent, false);
    return () => {
      window.removeEventListener("zopimMessageSent", handleMessageSent, false);
    };
  }, [handleMessageSent]);

  const handleChatClosed = useCallback(() => {
    // Telemetry: Chat closed via End Chat button
    createTelemetryEvent({
      feature: "human sme chat",
      action: "closed",
    }).fire(telemetryChannelName);
  }, []);

  useEffect(() => {
    window.addEventListener("zopimChatClosed", handleChatClosed, false);
    return () => {
      window.removeEventListener("zopimChatClosed", handleChatClosed, false);
    };
  }, [handleChatClosed]);

  return (
    <HasbotContext.Provider
      value={{
        showDialog,
        setShowDialog,
        techSupportActive,
        setTechSupportActive,
        aiChatActive,
        setAiChatActive,
        zopimChatActive,
        setZopimChatActive,
        keyboardShortcutsActive,
        setKeyboardShortcutsActive,
        openDialogBtnRef,
        unreadChatMessages,
      }}
    >
      {children}
    </HasbotContext.Provider>
  );
};
